import { navigateTo, navigationRef } from '@helpers';
import { RESTRICTED_ROOT_ROUTES, TENANT_REASON_TO_ROUTE, TenantReason, TenantStatusResult } from '@config/tenantRestrictions';
import storeModule from '@store';
import { sendLog } from '@reducers/dataDogs';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';
import type { StatusType } from '@reducers/dataDogs';

// TenantStatusResult.Unknown = 403 + JSON whose body couldn't be read (aborted mid-stream).
// Callers should treat it as "handled" but cannot navigate (no screen identified).

interface TenantReasonEntry {
  reason?: string;
  onboardingWizardCompleted?: boolean;
}

interface TenantReasonBody {
  reasons?: TenantReasonEntry[];
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isTenantReasonEntry = (value: unknown): value is TenantReasonEntry => {
  if (!isRecord(value)) return false;
  const { reason, onboardingWizardCompleted } = value;
  if (reason !== undefined && typeof reason !== 'string') return false;
  if (onboardingWizardCompleted !== undefined && typeof onboardingWizardCompleted !== 'boolean') return false;
  return true;
};

const isTenantReasonBody = (value: unknown): value is TenantReasonBody => {
  if (!isRecord(value)) return false;
  const { reasons } = value;
  if (reasons === undefined) return true;
  return Array.isArray(reasons) && reasons.every(isTenantReasonEntry);
};

const isRoutableTenantStatus = (result: TenantStatusResult): result is keyof typeof TENANT_REASON_TO_ROUTE => result in TENANT_REASON_TO_ROUTE;

const TENANT_RESULT_LOG: Partial<Record<TenantStatusResult, { title: string; status: StatusType }>> = {
  [TenantStatusResult.OnHold]: { title: LOGGER_TITLES.TENANT_ON_HOLD, status: 'info' },
  [TenantStatusResult.OnHoldWizard]: { title: LOGGER_TITLES.ONBOARDING_STARTED, status: 'info' },
  [TenantStatusResult.Deactivated]: { title: LOGGER_TITLES.TENANT_DEACTIVATED, status: 'warn' },
};

const recentlyLoggedTenantStatuses = new Set<TenantStatusResult>();

export const parseTenantReason = async (response: Response | null | undefined): Promise<TenantStatusResult | null> => {
  if (!response || typeof response.clone !== 'function') return null;
  // 2xx responses never carry a tenant-reason body — skip the clone + JSON parse cost on the happy path.
  if (response.ok) return null;
  const contentType = response.headers?.get?.('content-type') ?? '';
  if (!contentType.toLowerCase().includes('application/json')) return null;
  try {
    const cloned = response.clone();
    const parsed: unknown = await cloned.json();
    if (!isTenantReasonBody(parsed)) return null;
    const entry = parsed.reasons?.[0];
    const reason = entry?.reason;
    if (reason === TenantReason.OnHold) {
      // Backend may omit the flag for older builds — treat missing as completed (backwards-compat).
      return entry?.onboardingWizardCompleted === false ? TenantStatusResult.OnHoldWizard : TenantStatusResult.OnHold;
    }
    if (reason === TenantReason.Deactivated) return TenantStatusResult.Deactivated;
    return null;
  } catch (err) {
    // Aborted body read on a tenant-shaped response (component unmount during the
    // OnHold/Deactivated redirect) — distinguish from a real parse failure so the
    // caller skips the session-expired fallback instead of logging the user out.
    if (err instanceof Error && err.name === 'AbortError') return TenantStatusResult.Unknown;
    return null;
  }
};

export const handleTenantReasonResponse = async (input: Response | null | undefined): Promise<boolean> => {
  const result = await parseTenantReason(input);
  if (!result) return false;
  const logConfig = TENANT_RESULT_LOG[result];
  if (logConfig && !recentlyLoggedTenantStatuses.has(result)) {
    recentlyLoggedTenantStatuses.add(result);
    setTimeout(() => recentlyLoggedTenantStatuses.delete(result), 500);
    storeModule.store.dispatch(sendLog(logConfig));
  }
  if (isRoutableTenantStatus(result)) {
    const targetRoute = TENANT_REASON_TO_ROUTE[result];
    if (navigationRef.current?.getCurrentRoute()?.name !== targetRoute) navigateTo(targetRoute);
  }
  return true;
};

// After re-authentication / account-switch, the previous tenant may have left us on a
// restricted root (OnHold / OnboardingWizard / Deactivated / SettingsLimited). When the
// new tenant is OK, reset the stack to the default landing so the restricted screen is
// not reachable via browser back. We compare against the *root* active route rather
// than `getCurrentRoute()`, which returns the deepest leaf — SettingsLimited has a
// nested stack (Account, Users, etc.) whose leaf names would never match.
export const exitRestrictedRouteIfTenantOK = (tenantOK: boolean): void => {
  if (!tenantOK) return;
  const rootState = navigationRef.current?.getRootState();
  const activeRoot = rootState ? rootState.routes?.[rootState.index]?.name : undefined;
  if (activeRoot && (RESTRICTED_ROOT_ROUTES as readonly string[]).includes(activeRoot)) {
    navigationRef.current?.reset({ index: 0, routes: [{ name: 'Authorized' }] });
  }
};
